import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, Eye, Loader2, CheckCircle, User, ShoppingCart } from 'lucide-react';
import { format } from 'date-fns';
import { usePhotographerSales, Sale } from '@/hooks/usePhotographerSales';
import { PhotoPreviewModal } from '@/components/photo-preview-modal';

interface RecentSalesProps {
  photographerId: string;
}

export function RecentSales({ photographerId }: RecentSalesProps) {
  const [activeTab, setActiveTab] = useState<'pending' | 'paid' | 'all'>('pending');
  const [previewPhoto, setPreviewPhoto] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState<string | null>(null);
  
  // Cache para armazenar dados de cada aba
  const [cache, setCache] = useState<{
    pending: { sales: Sale[]; hasMore: boolean; loaded: boolean };
    paid: { sales: Sale[]; hasMore: boolean; loaded: boolean };
    all: { sales: Sale[]; hasMore: boolean; loaded: boolean };
  }>({
    pending: { sales: [], hasMore: false, loaded: false },
    paid: { sales: [], hasMore: false, loaded: false },
    all: { sales: [], hasMore: false, loaded: false },
  });

  const {
    sales,
    isLoading,
    error,
    hasMore,
    fetchSales,
    loadMore,
    confirmPaymentReceived,
  } = usePhotographerSales(photographerId);

  // Rastrear qual aba estava ativa quando os dados foram carregados
  const [lastLoadedTab, setLastLoadedTab] = React.useState<'pending' | 'paid' | 'all' | null>(null);

  // Quando o hook carrega dados inicialmente (aba pending por padrão), definir lastLoadedTab
  React.useEffect(() => {
    if (!isLoading && sales.length > 0 && lastLoadedTab === null && activeTab === 'pending') {
      setLastLoadedTab('pending');
    }
  }, [sales, isLoading, lastLoadedTab, activeTab]);

  // Atualizar cache quando sales mudar (apenas se os dados foram carregados para a aba atual)
  React.useEffect(() => {
    if (!isLoading && lastLoadedTab === activeTab) {
      const currentBillingStatus = activeTab === 'pending' ? 'Pendente' : activeTab === 'paid' ? 'Ok' : undefined;
      const filtered = currentBillingStatus 
        ? sales.filter(sale => sale.billingStatus === currentBillingStatus)
        : sales;
      
      setCache(prev => {
        const currentCache = prev[activeTab];
        // Se já tem dados no cache e estamos carregando mais (loadMore), adicionar aos existentes
        // Caso contrário, substituir (primeira carga)
        const isLoadMore = currentCache.loaded && currentCache.sales.length > 0 && 
                          filtered.length > currentCache.sales.length;
        
        return {
          ...prev,
          [activeTab]: {
            sales: isLoadMore 
              ? [...currentCache.sales, ...filtered.slice(currentCache.sales.length)]
              : filtered,
            hasMore: hasMore,
            loaded: true,
          },
        };
      });
    }
  }, [sales, hasMore, activeTab, isLoading, lastLoadedTab]);

  // Filtrar vendas baseado na aba ativa usando cache
  const filteredSales = useMemo(() => {
    return cache[activeTab].sales;
  }, [cache, activeTab]);

  // Verificar se há mais itens baseado no cache
  const cachedHasMore = useMemo(() => {
    return cache[activeTab].hasMore;
  }, [cache, activeTab]);

  const handleViewPhoto = (photo: any) => {
    setPreviewPhoto(photo);
    setIsPreviewOpen(true);
  };

  const handleConfirmPayment = async (orderId: string) => {
    if (!confirm('Tem certeza que deseja confirmar o recebimento deste pagamento? Esta ação é apenas para controle interno.')) {
      return;
    }

    try {
      setConfirmingPayment(orderId);
      await confirmPaymentReceived(orderId, photographerId);
      
      // Atualizar cache após confirmar pagamento
      setCache(prev => {
        const updated = { ...prev };
        
        // Encontrar a venda que foi confirmada
        let confirmedSale: Sale | null = null;
        
        // Procurar em todas as abas
        for (const tab of ['pending', 'paid', 'all'] as const) {
          const found = prev[tab].sales.find(sale => sale.id === orderId);
          if (found) {
            confirmedSale = { ...found, billingStatus: 'Ok' };
            break;
          }
        }
        
        if (confirmedSale) {
          // Remover da aba pending (se estiver lá)
          updated.pending.sales = prev.pending.sales.filter(sale => sale.id !== orderId);
          
          // Adicionar na aba paid (se não estiver lá)
          const alreadyInPaid = prev.paid.sales.some(sale => sale.id === orderId);
          if (!alreadyInPaid) {
            updated.paid.sales = [...prev.paid.sales, confirmedSale].sort((a, b) => {
              const dateA = new Date(a.createdat).getTime();
              const dateB = new Date(b.createdat).getTime();
              return dateB - dateA; // Mais recente primeiro
            });
          } else {
            // Se já está na aba paid, apenas atualizar o status
            updated.paid.sales = prev.paid.sales.map(sale =>
              sale.id === orderId ? confirmedSale! : sale
            );
          }
          
          // Atualizar na aba all também
          updated.all.sales = prev.all.sales.map(sale =>
            sale.id === orderId ? confirmedSale! : sale
          );
        }
        
        return updated;
      });
      
      alert('Recebimento confirmado com sucesso!');
    } catch (err) {
      console.error('Erro ao confirmar recebimento:', err);
      alert('Erro ao confirmar recebimento. Tente novamente.');
    } finally {
      setConfirmingPayment(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'bg-primary/10 text-primary border-primary/20';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTabChange = (value: string) => {
    const newTab = value as 'pending' | 'paid' | 'all';
    setActiveTab(newTab);
    
    // Só carregar se ainda não foi carregado (cache)
    if (!cache[newTab].loaded) {
      setLastLoadedTab(newTab);
      if (newTab === 'pending') {
        // Carregar apenas as primeiras 5 vendas pendentes (billingStatus = 'Pendente')
        fetchSales(photographerId, 'Pendente', 5, 0);
      } else if (newTab === 'paid') {
        // Carregar apenas as primeiras 5 vendas pagas (billingStatus = 'Ok')
        fetchSales(photographerId, 'Ok', 5, 0);
      } else {
        // Carregar as primeiras 5 vendas de todas
        fetchSales(photographerId, undefined, 5, 0);
      }
    }
  };

  const handleLoadMore = async () => {
    // Usar o comprimento das vendas do cache (já considera a aba ativa)
    const currentOffset = filteredSales.length;
    
    // Marcar que estamos carregando mais para esta aba
    setLastLoadedTab(activeTab);
    
    if (activeTab === 'pending') {
      await loadMore(photographerId, currentOffset, 'Pendente', 5);
    } else if (activeTab === 'paid') {
      await loadMore(photographerId, currentOffset, 'Ok', 5);
    } else {
      await loadMore(photographerId, currentOffset, undefined, 5);
    }
  };

  // Verificar se está carregando pela primeira vez (sem cache)
  const isInitialLoading = isLoading && !cache[activeTab].loaded && cache[activeTab].sales.length === 0;
  
  if (isInitialLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendas Recentes</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-sm text-muted-foreground">Carregando vendas...</p>
        </CardContent>
      </Card>
    );
  }

  if (error && sales.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendas Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">{error}</p>
          <Button
            onClick={() => {
              if (activeTab === 'pending') {
                fetchSales(photographerId, 'Pendente', 5, 0);
              } else if (activeTab === 'paid') {
                fetchSales(photographerId, 'Ok', 5, 0);
              } else {
                fetchSales(photographerId, undefined, 5, 0);
              }
            }}
            className="mt-4"
            size="sm"
          >
            Tentar Novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendas Recentes</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="pending">Vendas em Aberto</TabsTrigger>
            <TabsTrigger value="paid">Pagas</TabsTrigger>
            <TabsTrigger value="all">Todas</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {filteredSales.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600">
                  {activeTab === 'pending' 
                    ? 'Nenhuma venda pendente no momento.' 
                    : activeTab === 'paid'
                    ? 'Nenhuma venda paga encontrada.'
                    : 'Nenhuma venda encontrada.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSales.map((sale) => (
                  <Card key={sale.id} className="border-l-4 border-l-green-500">
                    {/* Cabeçalho da Venda */}
                    <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-primary" />
                        <div>
                          <h3 className="font-semibold text-lg">
                            Venda #{sale.ordernumber || sale.id.substring(0, 8)}
                          </h3>
                          <p className="text-sm text-primary">
                            {format(new Date(sale.createdat), 'dd/MM/yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="text-sm text-muted-foreground">Status do Pedido: </span>
                          <Badge className={getStatusColor(sale.status)}>
                            {sale.status === 'Paid' ? 'Pago' : sale.status === 'Pending' ? 'Pendente' : 'Cancelado'}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-muted-foreground">Status Faturamento: </span>
                          <Badge className={sale.billingStatus === 'Ok' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                            {sale.billingStatus === 'Ok' ? 'Ok' : 'Pendente'}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-muted-foreground">Valor: </span>
                          <span className="font-semibold text-lg">R$ {sale.totalAmount.toFixed(2)}</span>
                        </div>
                        {sale.billingStatus === 'Pendente' && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleConfirmPayment(sale.id)}
                            disabled={confirmingPayment === sale.id}
                            className="flex items-center gap-1 bg-primary hover:bg-secondary text-primary-foreground"
                          >
                            {confirmingPayment === sale.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle className="h-3 w-3" />
                            )}
                            <span className="text-xs">
                              {confirmingPayment === sale.id ? 'Confirmando...' : 'Confirmar Recebimento'}
                            </span>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Informações do Comprador */}
                    {sale.buyer && (
                      <div className="px-4 py-2 border-b bg-gray-50">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">Comprador:</span>
                          <span className="text-sm text-primary">{sale.buyer.name}</span>
                          <span className="text-xs text-muted-foreground">({sale.buyer.email})</span>
                        </div>
                      </div>
                    )}

                    {/* Itens da Venda */}
                    <div className="p-4">
                      <div className="space-y-2">
                        {sale.items.map((item, index) => (
                          <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-sm text-gray-500 w-8">#{index + 1}</span>

                              {/* Foto à esquerda */}
                              {item.photo?.storageurl && (
                                <div className="flex-shrink-0">
                                  <img
                                    src={item.photo.storageurl}
                                    alt={item.photo.original_filename || `Foto ${item.photoid.slice(-8)}`}
                                    className="w-12 h-12 object-cover rounded-lg"
                                  />
                                </div>
                              )}

                              {/* Informações à direita */}
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {item.photo?.original_filename || `Foto ${item.photoid.slice(-8)}`}
                                </p>
                                {item.event && (
                                  <p className="text-xs text-primary">
                                    Evento: {item.event.title}
                                  </p>
                                )}
                                {item.quantity > 1 && (
                                  <p className="text-xs text-muted-foreground">
                                    Quantidade: {item.quantity}
                                  </p>
                                )}
                              </div>

                              <div className="text-right">
                                <p className="text-sm font-medium">
                                  R$ {(item.unitprice * item.quantity).toFixed(2)}
                                </p>
                                {item.quantity > 1 && (
                                  <p className="text-xs text-muted-foreground">
                                    R$ {item.unitprice.toFixed(2)} cada
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              {item.photo && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewPhoto(item.photo)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="h-3 w-3" />
                                  <span className="text-xs">Visualizar</span>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
                
                {/* Botão "Ver mais" para ambas as abas quando houver mais itens */}
                {cachedHasMore && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      variant="outline"
                      className="w-full"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Carregando...
                        </>
                      ) : (
                        'Ver mais'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Modal de Visualização da Foto */}
        <PhotoPreviewModal
          photo={previewPhoto}
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewPhoto(null);
          }}
          onDownload={() => {
            // Download não disponível para fotógrafo
            console.log('Download não disponível para fotógrafo');
          }}
        />
      </CardContent>
    </Card>
  );
}

